if (!customElements.get('return-form-embed')) {
  class ReturnFormEmbed extends HTMLElement {
    static selectors = {
      target: '.return-form-embed__target',
    };

    constructor() {
      super();

      this.target = null;
      this.sectionId = '';
      this.pollId = '';
      this.closeEventName = 'return-form:close';
      this.closeRedirectUrl = '';
      this.abortController = null;
      this.observer = null;
      this.fallbackTimer = null;
      this.closeFallbackTimer = null;
      this.closeHandler = null;
      this.hasLoadedContent = false;
      this.hasClosed = false;
      this.pendingCloseReason = '';
      this.pendingCloseAt = 0;

      this.onTargetMutate = this.onTargetMutate.bind(this);
      this.onClose = this.onClose.bind(this);
      this.checkCloseFallback = this.checkCloseFallback.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;

      this.target = this.querySelector(ReturnFormEmbed.selectors.target);
      if (!this.target) return;

      this.sectionId = this.dataset.sectionId || '';
      this.pollId = this.dataset.pollId || '';
      this.closeEventName = this.dataset.closeEventName || 'return-form:close';
      this.closeRedirectUrl = this.dataset.closeRedirectUrl || '';

      this.abortController = new AbortController();

      this.setupLoadObserver();
      this.setupCloseBridge();
      this.registerCloseHandler();
      this.markLoaded();
    }

    disconnectedCallback() {
      this.unregisterCloseHandler();
      this.teardownLoadObserver();
      this.teardownCloseFallback();

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    setupLoadObserver() {
      this.teardownLoadObserver();

      this.observer = new MutationObserver(this.onTargetMutate);
      this.observer.observe(this.target, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      this.fallbackTimer = window.setInterval(this.onTargetMutate, 500);
    }

    teardownLoadObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.fallbackTimer) {
        window.clearInterval(this.fallbackTimer);
        this.fallbackTimer = null;
      }
    }

    onTargetMutate() {
      this.markLoaded();
    }

    hasRenderableContent() {
      if (!this.target) return false;

      return Boolean(this.target.children.length || this.target.textContent.trim());
    }

    markLoaded() {
      if (!this.hasRenderableContent()) return;

      this.hasLoadedContent = true;
      this.classList.add('is-loaded');
      this.teardownLoadObserver();
      this.setupCloseFallback();
    }

    setupCloseBridge() {
      window.Zigpoll = window.Zigpoll || {};
      window.__returnFormEmbedCloseHandlers = window.__returnFormEmbedCloseHandlers || [];

      if (window.__returnFormEmbedOnClosePatched) return;

      const previousOnClose = window.Zigpoll.onClose;

      window.Zigpoll.onClose = (payload) => {
        if (typeof previousOnClose === 'function') {
          previousOnClose(payload);
        }

        (window.__returnFormEmbedCloseHandlers || []).forEach((handler) => {
          if (typeof handler === 'function') {
            handler(payload);
          }
        });
      };

      window.__returnFormEmbedOnClosePatched = true;
    }

    registerCloseHandler() {
      this.unregisterCloseHandler();

      this.closeHandler = this.onClose;
      window.__returnFormEmbedCloseHandlers.push(this.closeHandler);
    }

    unregisterCloseHandler() {
      if (!this.closeHandler || !window.__returnFormEmbedCloseHandlers) return;

      window.__returnFormEmbedCloseHandlers = window.__returnFormEmbedCloseHandlers.filter((handler) => {
        return handler !== this.closeHandler;
      });

      this.closeHandler = null;
    }

    setupCloseFallback() {
      if (this.closeFallbackTimer || this.hasClosed) return;

      this.closeFallbackTimer = window.setInterval(this.checkCloseFallback, 500);
    }

    teardownCloseFallback() {
      if (this.closeFallbackTimer) {
        window.clearInterval(this.closeFallbackTimer);
        this.closeFallbackTimer = null;
      }

      this.pendingCloseReason = '';
      this.pendingCloseAt = 0;
    }

    getCloseFallbackReason() {
      if (!this.target || !this.hasLoadedContent) return '';

      const targetStyles = window.getComputedStyle(this.target);
      if (targetStyles.display === 'none' || targetStyles.visibility === 'hidden') {
        return 'target-hidden';
      }

      if (!this.hasRenderableContent()) {
        return 'target-empty';
      }

      const iframes = Array.from(this.target.querySelectorAll('iframe'));
      if (!iframes.length) return '';

      const hasVisibleIframe = iframes.some((iframe) => {
        const iframeStyles = window.getComputedStyle(iframe);
        return iframeStyles.display !== 'none' && iframeStyles.visibility !== 'hidden';
      });

      if (!hasVisibleIframe) {
        return 'iframe-hidden';
      }

      return '';
    }

    checkCloseFallback() {
      if (this.hasClosed || !this.isConnected) return;

      const closeReason = this.getCloseFallbackReason();
      if (!closeReason) {
        this.pendingCloseReason = '';
        this.pendingCloseAt = 0;
        return;
      }

      if (this.pendingCloseReason !== closeReason) {
        this.pendingCloseReason = closeReason;
        this.pendingCloseAt = Date.now();
        return;
      }

      if (Date.now() - this.pendingCloseAt < 1500) return;

      this.onClose({
        source: 'fallback',
        reason: closeReason,
      });
    }

    onClose(payload) {
      if (this.hasClosed) return;

      this.hasClosed = true;
      this.classList.add('is-loaded');
      this.teardownCloseFallback();

      const detail = {
        sectionId: this.sectionId,
        pollId: this.pollId,
        payload: payload || null,
      };

      window.dispatchEvent(new CustomEvent(this.closeEventName, { detail }));
      document.dispatchEvent(new CustomEvent(this.closeEventName, { detail }));

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'zigpoll_return_form_close',
          sectionId: this.sectionId,
          pollId: this.pollId,
        });
      }

      this.navigateBack();
    }

    navigateBack() {
      if (window.Shopify?.designMode) return;

      if (this.closeRedirectUrl) {
        window.location.assign(this.closeRedirectUrl);
        return;
      }

      const currentUrl = window.location.href;
      const referrer = document.referrer;
      const hasSafeReferrer = Boolean(referrer) && referrer !== currentUrl;

      if (hasSafeReferrer) {
        window.location.assign(referrer);
        return;
      }

      window.location.assign('/');
    }
  }

  customElements.define('return-form-embed', ReturnFormEmbed);
}
